import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  confirmationCopy,
  isStaleAcknowledgement,
  type DuplicateRoleResult,
  type RoleDuplicationPreview,
} from '@/lib/roleDuplication';
import {
  RoleDuplicationError,
  duplicateRole,
  fetchRoleDuplicationPreview,
} from '@/services/roleDuplicationService';

/**
 * RBAC-3.3 · FR-2, FR-2d. The confirm step of Duplicate: names every permission that will not be
 * copied, before the copy is made.
 *
 * Mounted by RBAC-3.1's `RolesPermissionsPage` from each role's Duplicate action, Joules roles and
 * the account's own alike.
 * Offer Duplicate on EVERY role. There is no un-duplicable role: Admin carries `manage_settings`
 * and must stay duplicable for RBAC-3.4.
 *
 * Nothing here is a control. `duplicate_role()` strips the reserved permissions itself and refuses
 * a copy whose acknowledgement does not match what it drops; this dialog sends the list it showed.
 */
export interface DuplicateRoleDialogProps {
  open: boolean;
  sourceRoleId: number | null;
  onOpenChange: (open: boolean) => void;
  onDuplicated: (result: DuplicateRoleResult) => void;
}

export function DuplicateRoleDialog({ open, sourceRoleId, onOpenChange, onDuplicated }: DuplicateRoleDialogProps) {
  const [preview, setPreview] = useState<RoleDuplicationPreview | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (id: number, keepName: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const p = await fetchRoleDuplicationPreview(id);
      setPreview(p);
      if (!keepName) setName(p.suggestedName);
    } catch (e) {
      setPreview(null);
      setError(e instanceof Error ? e.message : 'The role could not be read.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || sourceRoleId == null) return;
    setPreview(null);
    setDescription('');
    setNotice(null);
    void load(sourceRoleId, false);
  }, [open, sourceRoleId, load]);

  const confirm = async () => {
    if (!preview) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await duplicateRole(preview, name, description);
      onDuplicated(result);
      onOpenChange(false);
    } catch (e) {
      if (e instanceof RoleDuplicationError && isStaleAcknowledgement(e)) {
        // What would be dropped changed since this was shown. Show it again; never copy on a stale list.
        setNotice('What will not be copied has changed since this opened. Review it again before duplicating.');
        await load(preview.sourceRoleId, true);
      } else {
        setError(e instanceof Error ? e.message : 'The role could not be duplicated.');
      }
    } finally {
      setSaving(false);
    }
  };

  const copy = preview ? confirmationCopy(preview) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{copy?.title ?? 'Duplicate role'}</DialogTitle>
          {copy && <DialogDescription>{copy.summary}</DialogDescription>}
        </DialogHeader>

        {loading && !preview && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading the role…
          </div>
        )}

        {copy && (
          <div className="space-y-4">
            {copy.dropped.length > 0 && (
              <ul className="space-y-2 rounded-md border border-border bg-muted/40 p-3" aria-label="Permissions that will not be copied">
                {copy.dropped.map((line) => (
                  <li key={line.key} className="flex gap-2 text-sm">
                    <ShieldOff className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span>
                      <span className="font-medium">{line.label}</span>
                      {line.description && <span className="block text-muted-foreground">{line.description}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="duplicate-role-name">Name</Label>
              <Input
                id="duplicate-role-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={preview?.suggestedName}
                disabled={saving}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="duplicate-role-description">Description (optional)</Label>
              <Textarea
                id="duplicate-role-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={`Leave blank to keep ${preview?.sourceName}'s description`}
                rows={2}
                disabled={saving}
              />
            </div>
            <p className="text-sm text-muted-foreground">{copy.note}</p>
          </div>
        )}

        {notice && <p className="text-sm text-amber-600 dark:text-amber-400">{notice}</p>}
        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={!preview || loading || saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {copy?.confirmLabel ?? 'Duplicate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DuplicateRoleDialog;
